import React from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HubIcon from "@mui/icons-material/Hub";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    Alert,
    AlertTitle,
    Box,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

import styled from "styled-components";

import BaseOverview from "./BaseOverview";

// Interfaces
interface TabProps {
    // Add specific props if needed by your modal
}

// Styled Components
const StepCard = styled(Paper)`
    padding: 24px;
    margin-bottom: 24px;
    border-radius: 12px;
    border: 1px solid #e0e4e8;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
    transition: transform 0.2s ease;

    &:hover {
        border-color: #f66700;
    }
`;

const SectionHeader = styled(Box)`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
`;

const Screenshot = styled.img`
    width: 100%;
    border-radius: 8px;
    border: 1px solid #d1d9e0;
    margin-top: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const CodeTag = styled.code`
    background: #f1f3f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 0.85rem;
    color: #f66700;
`;

const AppsflyerOverview: React.FC<TabProps> = ({ mode, language }: any) => {
    if (mode === "standalone") return <BaseOverview language={language} />;
    return (
        <Box sx={{ p: 2 }}>
            {/* Header Section */}
            <Box mb={4}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <RocketLaunchIcon color="primary" />
                    <Typography
                        variant="h5"
                        fontWeight="800"
                        color="text.primary"
                    >
                        Finalize Your Tracker Setup
                    </Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                    Complete these high-priority configuration steps in your
                    AppsFlyer dashboard to ensure accurate data flow to
                    SignalSight.
                </Typography>
            </Box>

            {/* Step 1: Privacy */}
            <StepCard variant="outlined">
                <SectionHeader>
                    <SecurityIcon color="action" />
                    <Typography variant="h6" fontWeight="600">
                        1. Advanced Privacy Settings
                    </Typography>
                </SectionHeader>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    SignalSight requires standard attribution data to optimize
                    your campaigns.
                </Typography>
                <List disablePadding>
                    <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: "warning.main",
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Disable the 'Advanced Privacy' toggle"
                            primaryTypographyProps={{ variant: "body2" }}
                        />
                    </ListItem>
                </List>
            </StepCard>

            {/* Step 2: SignalSight App */}
            <StepCard variant="outlined">
                <SectionHeader>
                    <SettingsIcon color="action" />
                    <Typography variant="h6" fontWeight="600">
                        2. Configure the SignalSight App
                    </Typography>
                </SectionHeader>
                <List spacing={1}>
                    {[
                        {
                            text: "Enable the SignalSight App by selecting Active Partner.",
                            bold: "Active Partner",
                        },
                        {
                            text: "Select your desired tracking events in In-app Event Postbacks.",
                            bold: "In-app Event Postbacks",
                        },
                        {
                            text: "Enable the Values and Revenue checkbox.",
                            bold: "Values and Revenue",
                        },
                    ].map((item, index) => (
                        <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        bgcolor: "warning.main",
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    variant: "body2",
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Screenshot
                    src="/assets/images/appsflyer-dasboard.png"
                    alt="Dashboard Configuration"
                />
            </StepCard>

            {/* Step 3: Permissions */}
            <StepCard variant="outlined">
                <SectionHeader>
                    <HubIcon color="action" />
                    <Typography variant="h6" fontWeight="600">
                        3. Grant Permissions
                    </Typography>
                </SectionHeader>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Ensure the following permission toggles are enabled to allow
                    data synchronization:
                </Typography>
                <Screenshot
                    src="/assets/images/appsflyer-dashboard-2.png"
                    alt="Permissions View"
                />
            </StepCard>

            {/* Event Mappings Section */}
            <Box mb={4}>
                <Typography variant="h6" fontWeight="700" mb={2}>
                    Event Mappings
                </Typography>
                <Paper sx={{ p: 3, bgcolor: "#f8f9fa" }}>
                    <Typography variant="body2" mb={2}>
                        Navigate to{" "}
                        <strong>
                            SignalSight &gt; Trackers &gt; Event Mappings
                        </strong>{" "}
                        to align your naming conventions:
                    </Typography>
                    <Stack spacing={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <CodeTag>install</CodeTag>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                →
                            </Typography>
                            <Chip
                                label="Add to Cart"
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => {}}
                            />
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                            <CodeTag>af_purchase</CodeTag>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                →
                            </Typography>
                            <Chip
                                label="Purchase"
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => {}}
                            />
                        </Box>
                    </Stack>
                </Paper>
            </Box>

            {/* Next Action */}
            <Alert
                severity="primary"
                variant="outlined"
                sx={{ borderRadius: "8px" }}
            >
                <AlertTitle sx={{ fontWeight: 700 }}>
                    Ready for takeoff?
                </AlertTitle>
                Once these steps are complete, you can create your{" "}
                <strong>Landing Page</strong>.
                <Box mt={2}>
                    <Typography variant="caption" display="block" gutterBottom>
                        • Ensure your destination uses a{" "}
                        <strong>web dataset</strong> type.
                    </Typography>
                    <Typography variant="caption" display="block">
                        • Pro tip: Create a fresh web dataset for the most
                        accurate tracking.
                    </Typography>
                </Box>
            </Alert>
        </Box>
    );
};

export default AppsflyerOverview;
